import React, { useState } from 'react';
import { useQueries, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Modal } from '../../components/common/Modal';
import { type Recipe } from '../../types/recipe';
import { type InventoryItem } from '../../types/inventory';
import { recipeService } from '../../services/recipeService';
import { inventoryService } from '../../services/inventoryService';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { RoastForm } from '../roasts/component/RoastForm';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { Pagination } from '../../components/common/Pagination';
import { usePaginationState } from '../../hooks/usePaginationState';
import { RecipeFilters } from './components/RecipeFilters';
import { RecipesTable } from './components/RecipesTable';
import { RecipeMobileList } from './components/RecipeMobileList';
import { DeleteRecipeModal } from './components/DeleteRecipeModal';
import type { RecipeSortKey } from './components/types';

export const RecipesPage: React.FC = () => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canWrite = can('recipes', 'write');
  const canDelete = can('recipes', 'delete');
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | undefined>(undefined);
  const [nameFilter, setNameFilter] = useState('');
  const [itemCodeFilter, setItemCodeFilter] = useState<string[]>([]);
  const [roastTypeFilter, setRoastTypeFilter] = useState<string[]>([]);
  const [machineFilter, setMachineFilter] = useState<string[]>([]);

  const [sortConfig, setSortConfig] = useState<{ key: RecipeSortKey; direction: 'asc' | 'desc' } | null>(null);
  const [deletingRecipeId, setDeletingRecipeId] = useState<string | null>(null);
  const [recipeToDelete, setRecipeToDelete] = useState<Recipe | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { page, size, setPage, setSize, resetOnChange } = usePaginationState({ defaultSize: 25 });
  resetOnChange([
    nameFilter,
    sortConfig?.key,
    sortConfig?.direction,
    itemCodeFilter.join('|'),
    roastTypeFilter.join('|'),
    machineFilter.join('|'),
  ]);

  const { data: recipesResponse, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['recipes', { page, size, search: nameFilter }],
    queryFn: () => recipeService.list({ page, size, search: nameFilter }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  const recipes = React.useMemo(() => recipesResponse?.data ?? [], [recipesResponse?.data]);
  const total = recipesResponse?.total ?? 0;

  const { data: inventoryItems = [] } = useQuery({
    queryKey: ['inventory'],
    queryFn: () => inventoryService.getAll(),
    staleTime: 1000 * 60 * 5
  });

  const itemMap = React.useMemo(() => {
    return inventoryItems.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {} as Record<string, InventoryItem>);
  }, [inventoryItems]);

  const itemCodeMap = React.useMemo(() => {
    return inventoryItems.reduce((acc, item) => {
      const normalizedCode = item.code?.trim().toLowerCase();
      if (normalizedCode) {
        acc[normalizedCode] = item;
      }
      return acc;
    }, {} as Record<string, InventoryItem>);
  }, [inventoryItems]);

  const itemNameMap = React.useMemo(() => {
    return inventoryItems.reduce((acc, item) => {
      const normalizedName = item.name?.trim().toLowerCase();
      if (normalizedName) {
        acc[normalizedName] = item;
      }
      return acc;
    }, {} as Record<string, InventoryItem>);
  }, [inventoryItems]);

  const getRoastTypeLabel = React.useCallback((roastType?: string) => {
    if (!roastType) {
      return t('settingsMcp.values.none');
    }
    return t(`workOrders.beanSelection.roast.${roastType}`, undefined, roastType);
  }, [t]);

  const error = queryError ? t('recipes.page.errors.loadFailed') : null;

  const handleSort = (key: RecipeSortKey) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const recipesMissingItemInfo = React.useMemo(
    () =>
      recipes.filter(
        (recipe) =>
          !recipe.itemId &&
          !(recipe.itemName && recipe.itemName.trim()) &&
          !(recipe.itemCode && recipe.itemCode.trim()),
      ),
    [recipes],
  );

  const recipeDetailQueries = useQueries({
    queries: recipesMissingItemInfo.map((recipe) => ({
      queryKey: ['recipe', recipe.id, 'tableItemFallback'],
      queryFn: () => recipeService.getById(recipe.id),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const recipeDetailMap = React.useMemo(() => {
    const result: Record<string, Recipe> = {};
    recipeDetailQueries.forEach((query) => {
      if (query.data?.id) {
        result[query.data.id] = query.data;
      }
    });
    return result;
  }, [recipeDetailQueries]);

  const recipesWithDisplayData = React.useMemo(() => {
    return recipes.map((recipe) => {
      const detailedRecipe = recipeDetailMap[recipe.id];
      const resolvedItemId = recipe.itemId || detailedRecipe?.itemId || '';
      const resolvedItemName = recipe.itemName || detailedRecipe?.itemName || '';
      const resolvedItemCode = recipe.itemCode || detailedRecipe?.itemCode || '';

      const itemById = resolvedItemId ? itemMap[resolvedItemId] : undefined;
      const itemByCode = !itemById && resolvedItemCode
        ? itemCodeMap[resolvedItemCode.trim().toLowerCase()]
        : undefined;
      const itemByName = !itemById && !itemByCode && resolvedItemName
        ? itemNameMap[resolvedItemName.trim().toLowerCase()]
        : undefined;
      const resolvedItem = itemById || itemByCode || itemByName;

      const itemNameDisplay = resolvedItem?.name || resolvedItemName || t('settingsMcp.values.none');
      const itemCodeDisplay = resolvedItem?.code || resolvedItemCode || resolvedItemId || t('settingsMcp.values.none');
      const roastTypeDisplay = getRoastTypeLabel(recipe.roastType);
      const machineDisplay = recipe.machine || t('settingsMcp.values.none');

      return {
        recipe,
        itemNameDisplay,
        itemCodeDisplay,
        roastTypeDisplay,
        machineDisplay,
      };
    });
  }, [recipes, itemMap, itemCodeMap, itemNameMap, getRoastTypeLabel, t, recipeDetailMap]);

  const itemCodeOptions = React.useMemo(
    () =>
      Array.from(
        recipesWithDisplayData.reduce((acc, entry) => {
          if (entry.itemCodeDisplay === t('settingsMcp.values.none')) {
            return acc;
          }
          if (!acc.has(entry.itemCodeDisplay)) {
            acc.set(entry.itemCodeDisplay, {
              code: entry.itemCodeDisplay,
              name: entry.itemNameDisplay,
            });
          }
          return acc;
        }, new Map<string, { code: string; name: string }>()),
      )
        .map(([, option]) => option)
        .sort((a, b) => {
          const byName = a.name.localeCompare(b.name);
          if (byName !== 0) return byName;
          return a.code.localeCompare(b.code);
        }),
    [recipesWithDisplayData, t],
  );

  const roastTypeOptions = React.useMemo(
    () =>
      Array.from(
        new Set(
          recipesWithDisplayData
            .map((entry) => entry.recipe.roastType)
            .filter((value): value is string => Boolean(value)),
        ),
      ),
    [recipesWithDisplayData],
  );

  const machineOptions = React.useMemo(
    () =>
      Array.from(
        new Set(
          recipesWithDisplayData
            .map((entry) => entry.recipe.machine)
            .filter((value): value is string => Boolean(value)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [recipesWithDisplayData],
  );

  const filteredRecipes = React.useMemo(() => {
    const filtered = recipesWithDisplayData.filter((entry) => {
      const matchItemCode =
        itemCodeFilter.length === 0 || itemCodeFilter.includes(entry.itemCodeDisplay);
      const matchRoastType =
        roastTypeFilter.length === 0 || (entry.recipe.roastType && roastTypeFilter.includes(entry.recipe.roastType));
      const matchMachine =
        machineFilter.length === 0 || (entry.recipe.machine && machineFilter.includes(entry.recipe.machine));

      return matchItemCode && matchRoastType && matchMachine;
    });

    if (!sortConfig) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      const { key, direction } = sortConfig;

      const aValue: string | number =
        key === 'itemCodeDisplay'
          ? a.itemCodeDisplay
          : key === 'roastTypeDisplay'
            ? a.roastTypeDisplay
            : key === 'name'
              ? a.recipe.name
              : a.recipe.targetBeanWeight;
      const bValue: string | number =
        key === 'itemCodeDisplay'
          ? b.itemCodeDisplay
          : key === 'roastTypeDisplay'
            ? b.roastTypeDisplay
            : key === 'name'
              ? b.recipe.name
              : b.recipe.targetBeanWeight;

      if (aValue === bValue) return 0;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aText = String(aValue);
      const bText = String(bValue);
      return direction === 'asc' ? aText.localeCompare(bText) : bText.localeCompare(aText);
    });

    return sorted;
  }, [recipesWithDisplayData, itemCodeFilter, roastTypeFilter, machineFilter, sortConfig]);

  const handleOpenCreate = () => {
    setSelectedRecipe(undefined);
    setIsModalOpen(true);
  };

  const [isFetchingFullRecipe, setIsFetchingFullRecipe] = useState(false);

  const handleRowClick = async (recipe: Recipe) => {
    // Open modal immediately
    setIsModalOpen(true);
    setIsFetchingFullRecipe(true);
    setSelectedRecipe(recipe); // Initial data from list

    try {
      const fullRecipe = await queryClient.fetchQuery({
        queryKey: ['recipe', recipe.id],
        queryFn: () => recipeService.getById(recipe.id),
        staleTime: 1000 * 60 * 5
      });
      
      setSelectedRecipe(fullRecipe);
    } catch (err) {
      console.error('Failed to load recipe details:', err);
    } finally {
      setIsFetchingFullRecipe(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRecipe(undefined);
  };

  const handleOpenDeleteModal = (recipe: Recipe) => {
    setDeleteError(null);
    setRecipeToDelete(recipe);
  };

  const handleCloseDeleteModal = () => {
    if (deletingRecipeId) {
      return;
    }
    setRecipeToDelete(null);
    setDeleteError(null);
  };

  const handleDeleteRecipe = async () => {
    if (!recipeToDelete) {
      return;
    }
    setDeletingRecipeId(recipeToDelete.id);
    setDeleteError(null);
    try {
      await recipeService.delete(recipeToDelete.id);
      if (selectedRecipe?.id === recipeToDelete.id) {
        handleCloseModal();
      }
      await queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.removeQueries({ queryKey: ['recipe', recipeToDelete.id] });
      setRecipeToDelete(null);
    } catch (err) {
      console.error('Failed to delete recipe:', err);
      setDeleteError(t('recipes.page.errors.deleteFailed'));
    } finally {
      setDeletingRecipeId(null);
    }
  };

  if (loading && recipes.length === 0) {
    return <LoadingSpinner text={t('recipes.page.loading')} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-gray-900">{t('sidebar.masterProfile')}</h1>
        {canWrite && (
          <button
            data-test-id="recipe-create-button"
            onClick={handleOpenCreate}
            className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('recipes.page.actions.newProfile')}
          </button>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <div className="bg-white p-4 rounded-lg shadow-sm">
        <RecipeFilters
          search={nameFilter}
          onSearchChange={setNameFilter}
          itemCodeFilter={itemCodeFilter}
          onItemCodeChange={setItemCodeFilter}
          itemCodeOptions={itemCodeOptions}
          roastTypeFilter={roastTypeFilter}
          onRoastTypeChange={setRoastTypeFilter}
          roastTypeOptions={roastTypeOptions}
          machineFilter={machineFilter}
          onMachineChange={setMachineFilter}
          machineOptions={machineOptions}
          getRoastTypeLabel={getRoastTypeLabel}
        />

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          {filteredRecipes.length === 0 ? (
            <div className="px-4 py-5 sm:p-6 text-center text-gray-500">
              {t('recipes.page.empty')}
            </div>
          ) : (
            <>
              <RecipesTable
                entries={filteredRecipes}
                sortKey={sortConfig?.key}
                onSort={handleSort}
                canWrite={canWrite}
                canDelete={canDelete}
                deletingRecipeId={deletingRecipeId}
                onRowClick={handleRowClick}
                onDelete={handleOpenDeleteModal}
              />

              <Pagination page={page} pageSize={size} total={total} onPageChange={setPage} onPageSizeChange={setSize} isLoading={loading} className="hidden md:flex border-t" />

              <RecipeMobileList
                entries={filteredRecipes}
                canWrite={canWrite}
                canDelete={canDelete}
                deletingRecipeId={deletingRecipeId}
                onRowClick={handleRowClick}
                onDelete={handleOpenDeleteModal}
              />
              <Pagination page={page} pageSize={size} total={total} onPageChange={setPage} onPageSizeChange={setSize} isLoading={loading} className="md:hidden" />
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedRecipe ? t('recipes.page.actions.updateProfile') : t('recipes.page.actions.createNewProfile')}
        className="max-w-7xl"
      >
        {isFetchingFullRecipe ? (
          <div className="h-96 flex items-center justify-center">
            <LoadingSpinner text={t('recipes.page.fetchingDetails')} />
          </div>
        ) : (
          <RoastForm 
            mode="recipe" 
            initialData={selectedRecipe} 
            onSuccess={() => {
              handleCloseModal();
              queryClient.invalidateQueries({ queryKey: ['recipes'] });
              if (selectedRecipe) {
                queryClient.invalidateQueries({ queryKey: ['recipe', selectedRecipe.id] });
              }
            }}
            onCancel={handleCloseModal}
            readOnly={!canWrite}
          />
        )}
      </Modal>

      <DeleteRecipeModal
        recipe={recipeToDelete}
        error={deleteError}
        isDeleting={Boolean(deletingRecipeId)}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteRecipe}
      />
    </div>
  );
};
